// @flow
import React, { useRef, useState } from 'react'
import styles from './styles.css'

type Props = {
  selectedPath: ?string,
  uploading: boolean,
  onUpload: (params: {
    path: string,
    name: string,
    content_type: string,
    data: string,
  }) => Promise<*>,
}

const fileExtension = (name: string): string => {
  const dot = name.lastIndexOf('.')
  if (dot <= 0 || dot === name.length - 1) return ''
  return name.slice(dot + 1)
}

const ensureExtension = (typed: string, sourceName: string): string => {
  const ext = fileExtension(sourceName)
  if (!ext) return typed
  if (typed.toLowerCase().endsWith(`.${ext.toLowerCase()}`)) return typed
  return `${typed}.${ext}`
}

const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('Unexpected FileReader result'))
        return
      }
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error || new Error('FileReader error'))
    reader.readAsDataURL(file)
  })

const UploadBlock = ({ selectedPath, uploading, onUpload }: Props) => {
  const inputRef = useRef<?HTMLInputElement>(null)
  const [file, setFile] = useState<?File>(null)
  const [filename, setFilename] = useState('')
  const [dragOver, setDragOver] = useState(false)

  const acceptFile = (f: ?File) => {
    if (!f) return
    setFile(f)
    setFilename(f.name)
  }

  const openPicker = () => {
    if (inputRef.current) inputRef.current.click()
  }

  const onFileInput = (e: SyntheticInputEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0]
    acceptFile(f)
    if (inputRef.current) inputRef.current.value = ''
  }

  const onDrop = (e: SyntheticDragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files && e.dataTransfer.files[0]
    acceptFile(f)
  }

  const onDragOver = (e: SyntheticDragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(true)
  }

  const onDragLeave = () => setDragOver(false)

  const reset = () => {
    setFile(null)
    setFilename('')
  }

  const disabled =
    uploading || !file || !filename.trim() || !selectedPath

  const submit = async () => {
    if (disabled || !file || !selectedPath) return
    const data = await toBase64(file)
    try {
      await onUpload({
        path: selectedPath,
        name: ensureExtension(filename.trim(), file.name),
        content_type: file.type || 'application/octet-stream',
        data,
      })
      reset()
    } catch (_err) {
      // banner already dispatched by the thunk; keep filename for retry
    }
  }

  return (
    <div className={styles.uploadBlock}>
      <div className={styles.uploadLabel}>Upload Document</div>
      <div
        className={`${styles.dropzone} ${dragOver ? styles.dropzoneActive : ''}`}
        onClick={openPicker}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        role="button"
        tabIndex={0}
      >
        <svg
          className={styles.dropzoneIcon}
          width="42"
          height="42"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M17.5 19a4.5 4.5 0 0 0 .5-8.97A6 6 0 0 0 6.18 9.5 4.5 4.5 0 0 0 6.5 19" />
          <polyline points="12 12 12 19" />
          <polyline points="9 15 12 12 15 15" />
        </svg>
        <div className={styles.dropzoneText}>
          {file ? file.name : 'Drop here or click to browse'}
        </div>
        <input
          ref={inputRef}
          type="file"
          className={styles.hiddenInput}
          onChange={onFileInput}
        />
      </div>
      <div className={styles.uploadFilenameRow}>
        <input
          type="text"
          className={styles.uploadFilename}
          value={filename}
          onChange={(e) => setFilename(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !disabled) {
              e.preventDefault()
              submit()
            }
          }}
          placeholder="File name"
        />
        <button
          type="button"
          className={styles.uploadSubmit}
          onClick={submit}
          disabled={disabled}
        >
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
      </div>
    </div>
  )
}

export default UploadBlock
